/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Antigravity
 🏢 Company: Ring2All LLC
 📄 File: QueueCallbackReportPage.tsx
 🗓️ Created: 2026-01-16
 🧩 Description:
    Main page for Queue Callback Report with DataGrid, filters, and actions.
────────────────────────────────────────────────────────────────*/

import { useState, useMemo, useEffect } from 'react';
import { XCircle, RotateCcw, Trash2, RefreshCw, FilterX, PhoneCall } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { ModulePageContainer } from '../../components/layouts';
import { useNavigationStore } from '../../store/navigation';
import { useQueueCallbackList } from './hooks/useQueueCallbackList';
import { QueueCallback, CallbackStatus } from './types';
import { Button } from '../../components/ui/button';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import FixedActionBar from '../../components/ui/FixedActionBar';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { useNotifications } from '../../contexts/NotificationContext';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import DateRangePicker, { DateRange } from '../../components/ui/DateRangePicker';
import { Label } from '../../components/ui/label';
import { QueueSelect } from '../../components/ui/QueueSelect';

// Status badge colors
const STATUS_COLORS: Record<CallbackStatus, string> = {
    pending: 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400',
    calling: 'bg-blue-500/20 text-blue-600 dark:text-blue-400',
    scheduled: 'bg-purple-500/20 text-purple-600 dark:text-purple-400',
    completed: 'bg-green-500/20 text-green-600 dark:text-green-400',
    failed: 'bg-red-500/20 text-red-600 dark:text-red-400',
    cancelled: 'bg-gray-500/20 text-gray-600 dark:text-gray-400',
    expired: 'bg-orange-500/20 text-orange-600 dark:text-orange-400',
};

export default function QueueCallbackReportPage() {
    const { t } = useI18n();
    const { setCurrentPageTitle } = useNavigationStore();
    const { addNotification } = useNotifications();

    const [confirmDialog, setConfirmDialog] = useState<{
        open: boolean;
        type: 'cancel' | 'delete' | null;
        callback: QueueCallback | null;
    }>({ open: false, type: null, callback: null });

    const pageTitle = useMemo(() => t('queueCallbackReport.title'), [t]);

    useEffect(() => {
        setCurrentPageTitle(null);
        return () => setCurrentPageTitle(null);
    }, [setCurrentPageTitle]);

    const {
        data,
        loading,
        queues,
        columns: baseColumns,
        pagination,
        searchQuery,
        sortBy,
        sortOrder,
        filters,
        handleSearch,
        handleSort,
        handlePageChange,
        handleItemsPerPageChange,
        handleFilterChange,
        handleClearFilters,
        handleApplyFilters,
        handleCancel,
        handleRetry,
        handleDelete,
        refresh,
    } = useQueueCallbackList();

    // Add custom renders to columns
    const columns = useMemo(
        () =>
            baseColumns.map((column) => {
                if (column.key === 'createdAt' || column.key === 'nextAttemptAt') {
                    return {
                        ...column,
                        render: (_value: unknown, item: QueueCallback) => {
                            const dateValue = item[column.key as keyof QueueCallback] as string | null;
                            return (
                                <span className="text-sm text-foreground">
                                    {dateValue ? new Date(dateValue).toLocaleString() : '—'}
                                </span>
                            );
                        }
                    };
                }
                if (column.key === 'status') {
                    return {
                        ...column,
                        render: (_value: unknown, item: QueueCallback) => (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[item.status] || ''}`}>
                                {t(`queueCallbackReport.statuses.${item.status}`, { defaultValue: item.status })}
                            </span>
                        )
                    };
                }
                if (column.key === 'attemptCount') {
                    return {
                        ...column,
                        render: (_value: unknown, item: QueueCallback) => (
                            <span className="text-sm text-foreground">
                                {item.attemptCount} / {item.maxAttempts}
                            </span>
                        )
                    };
                }
                if (column.key === 'actions') {
                    return {
                        ...column,
                        render: (_value: unknown, item: QueueCallback) => (
                            <div className="flex items-center gap-1">
                                {/* Cancel button - only for pending/scheduled */}
                                {['pending', 'scheduled'].includes(item.status) && (
                                    <SimpleTooltip content={t('queueCallbackReport.actions.cancelTooltip')}>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setConfirmDialog({ open: true, type: 'cancel', callback: item });
                                            }}
                                        >
                                            <XCircle className="h-4 w-4 text-orange-500" />
                                        </Button>
                                    </SimpleTooltip>
                                )}

                                {/* Retry button - only for failed/expired/pending */}
                                {['pending', 'failed', 'expired'].includes(item.status) && (
                                    <SimpleTooltip content={t('queueCallbackReport.actions.retryTooltip')}>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            onClick={async (e) => {
                                                e.stopPropagation();
                                                const success = await handleRetry(item.id);
                                                addNotification({
                                                    type: success ? 'success' : 'error',
                                                    message: success
                                                        ? t('queueCallbackReport.messages.retrySuccess')
                                                        : t('queueCallbackReport.messages.retryError')
                                                });
                                            }}
                                        >
                                            <RotateCcw className="h-4 w-4 text-blue-500" />
                                        </Button>
                                    </SimpleTooltip>
                                )}

                                {/* Delete button */}
                                <SimpleTooltip content={t('queueCallbackReport.actions.deleteTooltip')}>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setConfirmDialog({ open: true, type: 'delete', callback: item });
                                        }}
                                    >
                                        <Trash2 className="h-4 w-4 text-destructive" />
                                    </Button>
                                </SimpleTooltip>
                            </div>
                        )
                    };
                }
                return column;
            }),
        [baseColumns, t, handleRetry, addNotification]
    );

    // Handle confirmation actions
    const handleConfirmAction = async () => {
        if (!confirmDialog.callback) return;

        let success = false;
        if (confirmDialog.type === 'cancel') {
            success = await handleCancel(confirmDialog.callback.id);
            addNotification({
                type: success ? 'success' : 'error',
                message: success
                    ? t('queueCallbackReport.messages.cancelSuccess')
                    : t('queueCallbackReport.messages.cancelError')
            });
        } else if (confirmDialog.type === 'delete') {
            success = await handleDelete(confirmDialog.callback.id);
            addNotification({
                type: success ? 'success' : 'error',
                message: success
                    ? t('queueCallbackReport.messages.deleteSuccess')
                    : t('queueCallbackReport.messages.deleteError')
            });
        }

        setConfirmDialog({ open: false, type: null, callback: null });
    };

    // Check if any filter has been set
    const hasActiveFilters = !!(
        filters.queueId ||
        filters.status ||
        filters.dateFrom ||
        filters.dateTo
    );

    return (
        <>
            <ModulePageContainer
                currentView="list"
                listView={
                    <div className="queue-callback-wrapper">
                        {/* Filters Card - Below fixed header */}
                        <div className="pt-16">
                            <div className="p-3 bg-card/75 backdrop-blur-md border border-border/50 rounded-lg shadow-sm">
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">

                                    {/* Row 1: Queue (searchable) | Status */}
                                    <Label className="text-left">
                                        {t('queueCallbackReport.filters.queue')}
                                    </Label>
                                    <QueueSelect
                                        value={filters.queueId || ''}
                                        onChange={(id) => handleFilterChange('queueId', id)}
                                        options={queues.map(q => ({ id: String(q.id), name: q.name }))}
                                    />

                                    <Label className="text-left">
                                        {t('queueCallbackReport.filters.status')}
                                    </Label>
                                    <select
                                        className="h-9 px-3 rounded-md border border-input bg-background text-sm"
                                        value={filters.status || ''}
                                        onChange={(e) => handleFilterChange('status', e.target.value)}
                                    >
                                        <option value="">{t('queueCallbackReport.filters.allStatuses')}</option>
                                        <option value="pending">{t('queueCallbackReport.statuses.pending')}</option>
                                        <option value="calling">{t('queueCallbackReport.statuses.calling')}</option>
                                        <option value="completed">{t('queueCallbackReport.statuses.completed')}</option>
                                        <option value="failed">{t('queueCallbackReport.statuses.failed')}</option>
                                        <option value="cancelled">{t('queueCallbackReport.statuses.cancelled')}</option>
                                        <option value="expired">{t('queueCallbackReport.statuses.expired')}</option>
                                    </select>

                                    {/* Row 2: Date Range */}
                                    <Label className="text-left">
                                        {t('queueCallbackReport.filters.dateRange') || 'Date Range'}
                                    </Label>
                                    <DateRangePicker
                                        value={{
                                            startDate: filters.dateFrom || new Date().toISOString().split('T')[0],
                                            endDate:   filters.dateTo   || new Date().toISOString().split('T')[0],
                                            startTime: '00:00',
                                            endTime:   '23:59',
                                        }}
                                        onChange={(range: DateRange) => {
                                            handleFilterChange('dateFrom', range.startDate);
                                            handleFilterChange('dateTo',   range.endDate);
                                        }}
                                    />

                                </div>  {/* end grid */}
                            </div>  {/* end p-3 */}
                        </div>  {/* end pt-16 */}

                        {/* Data Grid */}
                        <DataGridSimple
                            title={t('queueCallbackReport.title')}
                            titleIcon={<PhoneCall className="h-5 w-5 text-primary" />}
                            data={data}
                            columns={columns}
                            loading={loading}
                            pagination={pagination}
                            onPageChange={handlePageChange}
                            onItemsPerPageChange={handleItemsPerPageChange}
                            onColumnToggle={() => { }}
                            onSort={handleSort}
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            searchQuery={searchQuery}
                            onSearch={handleSearch}
                            searchPlaceholder={t('queueCallbackReport.searchPlaceholder')}
                            emptyMessage={t('queueCallbackReport.messages.noData')}
                        />

                        <style dangerouslySetInnerHTML={{
                            __html: `
                            .queue-callback-wrapper {
                                display: flex;
                                flex-direction: column;
                                gap: 0;
                            }
                            
                            .queue-callback-wrapper .space-y-6 > :not([hidden]) ~ :not([hidden]) {
                                margin-top: 0 !important;
                            }
                        `}} />
                    </div>
                }
                formView={null}
            />

            {/* Fixed Action Bar */}
            <FixedActionBar
                onReset={hasActiveFilters ? handleClearFilters : undefined}
                onSave={handleApplyFilters}
                resetText={
                    <>
                        <FilterX className="h-4 w-4 mr-2" />
                        {t('queueCallbackReport.filters.clear')}
                    </>
                }
                saveText={
                    <>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        {t('queueCallbackReport.filters.refresh')}
                    </>
                }
                showCancel={false}
                showDelete={false}
                isSaving={loading}
            />

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={confirmDialog.open}
                onClose={() => setConfirmDialog({ open: false, type: null, callback: null })}
                onConfirm={handleConfirmAction}
                title={
                    confirmDialog.type === 'cancel'
                        ? t('queueCallbackReport.confirmations.cancelTitle')
                        : t('queueCallbackReport.confirmations.deleteTitle')
                }
                message={
                    confirmDialog.type === 'cancel'
                        ? t('queueCallbackReport.confirmations.cancelMessage')
                        : t('queueCallbackReport.confirmations.deleteMessage')
                }
                confirmText={
                    confirmDialog.type === 'cancel'
                        ? t('queueCallbackReport.actions.cancel')
                        : t('queueCallbackReport.actions.delete')
                }
                type={confirmDialog.type === 'delete' ? 'danger' : 'warning'}
            />
        </>
    );
}
